import { SelectOption } from '@michelin/acid-components';
import {
  LoadRange,
  ProductOVM,
  ResponseOVM,
  TireProfileAxle,
  TireProfileAxles,
  TireProfileOVM,
  TireProfilePriorities,
} from '@michelin/fcp-view-models';
import { getOptions } from '@michelin/select-options-provider';
import { AXLES, AxleName } from '../types';

export const getAxle = (axleName: AxleName, tireProfile: TireProfileOVM): TireProfileAxle | null => {
  switch (axleName) {
    case 'all_axles':
      return tireProfile.axles?.all_axles || null;
    case 'carry':
      return tireProfile.axles?.carry || null;
    case 'tag':
      return tireProfile.axles?.tag || null;
    case 'trailer':
      return tireProfile.axles?.trailer || null;
    case 'steer':
      return tireProfile.axles?.steer || null;
    case 'pusher':
      return tireProfile.axles?.pusher || null;
    case 'lift':
      return tireProfile.axles?.lift || null;
    case 'drive':
      return tireProfile.axles?.drive || null;
  }
};

export const filterTheBrand = (brands: SelectOption[], isOnsite: boolean, priority: any): SelectOption[] => {
  if (priority === 1) {
    if (isOnsite) {
      return brands.filter((x) => /oliver|uniroyal|mrt|michelin|bfgoodrich/gi.test(x.value));
    } else {
      return brands.filter((x) => /mrt|michelin|bfgoodrich/gi.test(x.value));
    }
  }
  return brands;
};

interface EqualProfile {
  tireSize: string;
  axle: string;
}

export interface UniqueErrorData {
  [profileName: string]: EqualProfile[];
}

export const ErrorHandler = (
  res: ResponseOVM<TireProfileOVM>,
): { isError: boolean; data?: TireProfileOVM | undefined; errors?: string[] } => {
  if (res.error && res.error === 'Duplicated profile') {
    if (res.errors && res.errors.length > 0) {
      throw new Error(JSON.stringify(res));
    }
    throw new Error(JSON.stringify({ isUniqueError: true, uniqueErrorData: [] }));
  } else {
    if (res.errors && res.errors?.length > 0) {
      const errorMessages: string[] = [];

      res.errors.forEach((error) => {
        const field = error.field?.split('.');
        let fieldMessage = '{{MAIN}}: {{AXLE}} - Field: {{FIELD}}';

        if (!field && error.message) {
          errorMessages.push(error.message);
        }

        if (field && field[0] === 'axles') {
          fieldMessage = fieldMessage.replace('{{MAIN}}', 'Axle');
          fieldMessage = fieldMessage.replace('{{AXLE}}', AXLES.find((axle) => axle.name === field[1])?.label || '');

          for (let i = 2; i <= field.length - 1; i++) {
            fieldMessage = fieldMessage.replace('{{FIELD}}', `${field[i]} {{FIELD}}`);
          }

          fieldMessage = fieldMessage.replace(' {{FIELD}}', ':');

          errorMessages.push(error.message?.replace(`"${error.field}"`, fieldMessage) || '');
        }
      });
      throw new Error(JSON.stringify({ isUniqueError: false, errorList: errorMessages }));
    }
  }
  return { isError: false, data: res.data };
};

export const getAutocompleteValues = (values: string[] | undefined, options: SelectOption[]): SelectOption[] => {
  if (!values) return [];
  return options.filter((opt) => values.includes(opt.value));
};

export const getAxleFromAxles = (axleName: AxleName, axles: TireProfileAxles): TireProfileAxle | null => {
  switch (axleName) {
    case 'all_axles':
      return axles?.all_axles || null;
    case 'carry':
      return axles?.carry || null;
    case 'tag':
      return axles?.tag || null;
    case 'trailer':
      return axles?.trailer || null;
    case 'steer':
      return axles?.steer || null;
    case 'pusher':
      return axles?.pusher || null;
    case 'lift':
      return axles?.lift || null;
    case 'drive':
      return axles?.drive || null;
  }
};
export const sortedAxlesBySizeAndPriority = (axles: TireProfileAxles | undefined) => {
  if (!axles || Object.keys(axles).length === 0) return axles;
  const equalSizes = getOptions('equal_sizes').map((option: any) => option.value);

  Object.keys(axles).forEach((key) => {
    const axle = getAxleFromAxles(key as AxleName, axles);
    if (axle && axle.tire_replacement_priority) {
      // 1. Sort by Tire Size, then by Priority
      axle.tire_replacement_priority.sort((a, b) => {
        // First sorted by tire_size
        if (a.tire_size && b.tire_size) {
          if (!doWeEqual(a.tire_size, b.tire_size, equalSizes)) {
            if (a.tire_size < b.tire_size) return -1;
            if (a.tire_size > b.tire_size) return 1;
          }
        }
        // if same tire_size (or equivalent), sorted by priority
        if (a.priority && b.priority) {
          if (a.priority < b.priority) return -1;
          if (a.priority > b.priority) return 1;
        }
        return 0; // if same tire_size and priority
      });

      // 2. Normalize Priorities (1..N) per Tire Size Group
      let currentSize = '';
      let currentPriorityCounter = 1;

      axle.tire_replacement_priority.forEach((item) => {
        const itemSize = item.tire_size || '';
        if (currentSize === '' || !doWeEqual(itemSize, currentSize, equalSizes)) {
          // New group detected (and not equal to previous), reset counter
          currentSize = itemSize;
          currentPriorityCounter = 1;
        }
        // Assign sequential priority
        item.priority = currentPriorityCounter;
        currentPriorityCounter++;
      });
    }
  });
  return axles;
};

// Older profiles (and catalog entries from before select-options-provider@1.1.71) can carry a
// trailing commercial/load-range letter suffix that current catalog data no longer has, e.g.
// "225/75R16C" -> "225/75R16", "LT245/70R17/E" -> "LT245/70R17". Stripping it keeps those
// profiles matching the catalog instead of showing as "not available in catalog".
export const normalizeTireSize = (size?: string | null): string =>
  (size || '')
    .trim()
    .toUpperCase()
    .replace(/(\d)\/?[A-Z]$/, '$1');

// Case/whitespace-insensitive comparison for brand and tread names, to tolerate old formatting
// (e.g. "Michelin" vs "MICHELIN", "AGILIS CROSSCLIMATE2" vs "Agilis CrossClimate 2").
export const normalizeCatalogText = (value?: string | null): string => (value || '').trim().toUpperCase().replace(/\s+/g, '');

export const doWeEqual = (a: string, b: string, equalSizes: string[]) => {
  let ret = false;
  const normA = normalizeTireSize(a);
  const normB = normalizeTireSize(b);

  if (normA === normB) {
    return true;
  }
  equalSizes.forEach((e) => {
    const testString = e.split('~');
    const opt0 = normalizeTireSize(testString[0]);
    const opt1 = normalizeTireSize(testString[1]);
    if ((opt0 === normA || opt1 === normA) && (opt0 === normB || opt1 === normB)) {
      ret = true;
    }
  });
  return ret;
};

export const getTheOtherEqualSize = (size: string, equalSizes: string[]) => {
  let ret: string | undefined = undefined;
  equalSizes.forEach((e) => {
    const testString = e.split('~');
    if (testString[0].toUpperCase() === size.toUpperCase()) {
      ret = testString[1];
    } else if (testString[1].toUpperCase() === size.toUpperCase()) {
      ret = testString[0];
    }
  });
  return ret;
};

export const updatePriorities = (
  replacementList: TireProfilePriorities[],
  tireSize: string,
  otherEqualSize: string | undefined,
): TireProfilePriorities[] => {
  let auxPriority = 0;
  const newReplacementPriority = replacementList.map((item) => {
    if (item.tire_size === tireSize || otherEqualSize === item.tire_size) {
      auxPriority += 1;
      return { ...item, priority: auxPriority };
    } else {
      return item;
    }
  });
  return newReplacementPriority;
};

export interface CatalogMatch {
  product: ProductOVM;
  size: NonNullable<ProductOVM['sizes']>[number];
  tread: NonNullable<NonNullable<ProductOVM['sizes']>[number]['treads']>[number];
  load: LoadRange;
}

// Old profiles can carry a brand/tread name in a format the current catalog no longer uses (e.g.
// "Agilis CrossClimate 2" saved when the catalog had full names, now stored as "AGILISCC2") -
// something no amount of case/whitespace normalization can bridge. MSPN is the one identifier
// that never changes format, so it's the most reliable way to find the exact catalog entry for a
// saved row and re-sync its display fields.
export const findCatalogMatchByMspn = (products: ProductOVM[], mspn?: string | null): CatalogMatch | null => {
  if (!mspn) return null;
  for (const product of products) {
    for (const size of product.sizes || []) {
      for (const tread of size.treads || []) {
        const load = tread.loads?.find((l) => l.mspn === mspn);
        if (load) return { product, size, tread, load };
      }
    }
  }
  return null;
};

export const doSizesEqual = (a: string, b: string, equalSizes: string[]) => {
  if (doWeEqual(a, b, equalSizes)) {
    return true;
  }
  return a.toUpperCase() === b.toUpperCase();
};

export const getOtd32 = (value: string): string => {
  const otd32 = /\/32/g.test(value || '') ? value : (value && /^\s$/.test(value)) || !value ? '' : `${value}/32`;
  return otd32;
};
